import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

export function formatCurrency(amount, currency = 'GBP') {
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currency,
    }).format(amount);
}

export function formatDate(dateString) {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    }).format(date);
}

export function formatDateTime(dateString) {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
}

export function getInitials(name) {
    if (!name) return '?';
    return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

export function truncate(str, length = 50) {
    if (!str) return '';
    if (str.length <= length) return str;
    return str.slice(0, length) + '...';
}

export function getStatusColor(status) {
    const colors = {
        // Application statuses
        applied: 'bg-blue-100 text-blue-700 border-blue-200',
        interview: 'bg-amber-100 text-amber-700 border-amber-200',
        rejected: 'bg-rose-100 text-rose-700 border-rose-200',
        offered: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        // Order statuses
        pending: 'bg-amber-100 text-amber-700 border-amber-200',
        in_progress: 'bg-blue-100 text-blue-700 border-blue-200',
        completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        // Payment statuses
        paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        failed: 'bg-rose-100 text-rose-700 border-rose-200',
        // Subscription statuses
        active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        cancelled: 'bg-neutral-100 text-neutral-700 border-neutral-200',
        expired: 'bg-rose-100 text-rose-700 border-rose-200',
        // CV submission statuses
        viewed: 'bg-blue-100 text-blue-700 border-blue-200',
        contacted: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        // Consultation statuses
        scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
    };
    return colors[status] || 'bg-neutral-100 text-neutral-700 border-neutral-200';
}

export const INDUSTRIES = [
    'Technology',
    'Healthcare',
    'Finance',
    'Engineering',
    'Marketing',
    'Sales',
    'Hospitality',
    'Retail',
    'Construction',
    'Education',
    'Legal',
    'Manufacturing',
    'Logistics',
    'Creative',
    'Administrative',
];

export const JOB_TYPES = [
    { value: 'permanent', label: 'Permanent' },
    { value: 'temporary', label: 'Temporary' },
    { value: 'contract', label: 'Contract' },
];

// Organized by category for better UX
export const JOB_ROLE_CATEGORIES = {
    'Office, Admin & Support': [
        'Administrative Assistant',
        'Office Administrator',
        'Office Manager',
        'Receptionist',
        'Personal Assistant (PA)',
        'Executive Assistant',
        'Data Entry Clerk',
        'Clerical Officer',
        'Operations Administrator',
        'Business Support Officer',
        'Facilities Coordinator',
        'Facilities Manager',
        'Records Officer',
        'Document Controller',
        'Typist',
        'Office Coordinator',
        'Mailroom Assistant',
        'Switchboard Operator',
        'Secretary',
        'Team Administrator',
        'Project Administrator',
        'Legal Secretary',
        'Medical Secretary',
        'School Administrator',
        'HR Administrator',
    ],
    'Finance, Accounting & Banking': [
        'Accounts Assistant',
        'Accounts Payable Clerk',
        'Accounts Receivable Clerk',
        'Finance Officer',
        'Finance Manager',
        'Financial Analyst',
        'Management Accountant',
        'Financial Accountant',
        'Payroll Officer',
        'Payroll Manager',
        'Credit Controller',
        'Bookkeeper',
        'Auditor',
        'Internal Auditor',
        'Tax Assistant',
        'Tax Advisor',
        'Risk Analyst',
        'Compliance Officer',
        'Banking Associate',
        'Mortgage Advisor',
    ],
    'Legal, Compliance & Governance': [
        'Paralegal',
        'Legal Assistant',
        'Solicitor',
        'Barrister',
        'Legal Advisor',
        'Compliance Analyst',
        'Compliance Manager',
        'Governance Officer',
        'Data Protection Officer',
        'Information Governance Officer',
        'Privacy Analyst',
        'FOI Officer',
        'Contracts Manager',
        'Regulatory Affairs Officer',
        'Case Officer',
    ],
    'IT, Tech & Digital': [
        'IT Support Analyst',
        'IT Support Technician',
        'Service Desk Analyst',
        'Systems Administrator',
        'Network Engineer',
        'Cloud Engineer',
        'DevOps Engineer',
        'Software Developer',
        'Frontend Developer',
        'Backend Developer',
        'Full Stack Developer',
        'Web Developer',
        'Mobile App Developer',
        'QA Tester',
        'Automation Tester',
        'Cyber Security Analyst',
        'Information Security Analyst',
        'SOC Analyst',
        'Data Analyst',
        'Business Intelligence Analyst',
        'Data Engineer',
        'Data Scientist',
        'AI Engineer',
        'Machine Learning Engineer',
        'Product Manager',
        'Technical Product Manager',
        'Scrum Master',
        'Agile Coach',
        'UX Designer',
        'UI Designer',
    ],
    'Business, Management & Strategy': [
        'Business Analyst',
        'Business Manager',
        'Operations Manager',
        'General Manager',
        'Programme Manager',
        'Project Manager',
        'Change Manager',
        'Transformation Manager',
        'Strategy Consultant',
        'Management Consultant',
        'Process Improvement Analyst',
        'Performance Manager',
        'Commercial Manager',
        'Contract Manager',
        'Procurement Officer',
        'Procurement Manager',
        'Supply Chain Analyst',
        'Supply Chain Manager',
        'Vendor Manager',
        'Stakeholder Manager',
        'Client Relationship Manager',
        'Account Manager',
        'Key Account Manager',
        'Business Development Executive',
        'Business Development Manager',
    ],
    'Marketing, Media & Creative': [
        'Marketing Assistant',
        'Marketing Executive',
        'Marketing Manager',
        'Digital Marketing Executive',
        'Digital Marketing Manager',
        'SEO Specialist',
        'PPC Specialist',
        'Social Media Manager',
        'Content Writer',
        'Copywriter',
        'Brand Manager',
        'Communications Officer',
        'PR Officer',
        'PR Manager',
        'Graphic Designer',
        'Motion Graphics Designer',
        'Video Editor',
        'Photographer',
        'Creative Director',
        'Campaign Manager',
    ],
    'Sales, Retail & Customer Service': [
        'Sales Assistant',
        'Retail Assistant',
        'Store Assistant',
        'Store Manager',
        'Assistant Store Manager',
        'Sales Executive',
        'Sales Manager',
        'Field Sales Representative',
        'Business Sales Executive',
        'Account Executive',
        'Customer Service Assistant',
        'Customer Service Advisor',
        'Contact Centre Agent',
        'Call Centre Agent',
        'Customer Support Analyst',
        'Client Services Officer',
        'Customer Success Manager',
        'Telesales Executive',
        'E-commerce Assistant',
        'Merchandiser',
    ],
    'Health, Care & Social Services': [
        'Healthcare Assistant',
        'Support Worker',
        'Care Assistant',
        'Senior Care Assistant',
        'Care Coordinator',
        'Registered Nurse',
        'Mental Health Nurse',
        'Midwife',
        'Social Worker',
        'Occupational Therapist',
        'Physiotherapist',
        'Speech and Language Therapist',
        'Clinical Support Worker',
        'Pharmacy Assistant',
        'Pharmacy Technician',
        'GP Receptionist',
        'Practice Manager',
        'Health Administrator',
        'Public Health Officer',
        'Community Outreach Worker',
    ],
    'Construction, Engineering & Skilled Trades': [
        'Labourer',
        'Construction Operative',
        'Site Supervisor',
        'Site Manager',
        'Project Engineer',
        'Civil Engineer',
        'Structural Engineer',
        'Mechanical Engineer',
        'Electrical Engineer',
        'Maintenance Engineer',
        'Electrician',
        'Plumber',
        'Carpenter',
        'Bricklayer',
        'Painter & Decorator',
        'Quantity Surveyor',
        'Estimator',
        'Building Inspector',
        'Facilities Engineer',
        'Health & Safety Officer',
    ],
    'Transport, Logistics & Warehouse': [
        'Warehouse Operative',
        'Forklift Driver',
        'HGV Driver',
        'Delivery Driver',
        'Logistics Coordinator',
    ],
};

// Flat list of all job roles for searching
export const JOB_ROLES = Object.values(JOB_ROLE_CATEGORIES).flat();

// UK Cities for agency locations
export const UK_CITIES = [
    // England - Major Cities
    'London',
    'Birmingham',
    'Manchester',
    'Leeds',
    'Liverpool',
    'Newcastle upon Tyne',
    'Sheffield',
    'Bristol',
    'Nottingham',
    'Leicester',
    'Southampton',
    'Brighton',
    'Portsmouth',
    'Reading',
    'Milton Keynes',
    'Coventry',
    'Bradford',
    'Stoke-on-Trent',
    'Wolverhampton',
    'Plymouth',
    'Derby',
    'Hull',
    'Luton',
    'Blackpool',
    'Bolton',
    'Bournemouth',
    'Middlesbrough',
    'Sunderland',
    'Norwich',
    'Oxford',
    'Cambridge',
    'York',
    'Exeter',
    'Gloucester',
    'Chester',
    'Bath',
    'Worcester',
    'Lincoln',
    'Canterbury',
    'Peterborough',
    'Ipswich',
    'Colchester',
    'Chelmsford',
    'Northampton',
    'Warrington',
    'Slough',
    'Blackburn',
    'Preston',
    'Huddersfield',
    'Wigan',
    'Rochdale',
    'Stockport',
    'Oldham',
    'Birkenhead',
    'Watford',
    'Basingstoke',
    'Guildford',
    'Woking',
    'Crawley',
    'Maidstone',
    'St Albans',
    'High Wycombe',
    'Telford',
    'Swindon',
    'Doncaster',
    'Rotherham',
    'Barnsley',
    'Wakefield',
    'Salford',
    // England - Additional Cities & Towns
    'Stevenage',
    'Harlow',
    'Basildon',
    'Southend-on-Sea',
    'Cheltenham',
    'Eastbourne',
    'Hastings',
    'Worthing',
    'Folkestone',
    'Dover',
    'Ashford',
    'Tunbridge Wells',
    'Sevenoaks',
    'Dartford',
    'Gravesend',
    'Chatham',
    'Rochester',
    'Gillingham',
    'Margate',
    'Ramsgate',
    'Deal',
    'Aylesbury',
    'Banbury',
    'Bedford',
    'Welwyn Garden City',
    'Hemel Hempstead',
    'Hatfield',
    'Hitchin',
    'Letchworth',
    'Royston',
    'Bishops Stortford',
    'Harrogate',
    'Scarborough',
    'Whitby',
    'Skipton',
    'Ripon',
    'Selby',
    'Beverley',
    'Grimsby',
    'Scunthorpe',
    'Cleethorpes',
    'Grantham',
    'Boston',
    'Spalding',
    'Skegness',
    'Stamford',
    'Louth',
    'Gainsborough',
    'Newark-on-Trent',
    'Retford',
    'Worksop',
    'Mansfield',
    'Chesterfield',
    'Buxton',
    'Matlock',
    'Ashbourne',
    'Glossop',
    'Bakewell',
    'Burton upon Trent',
    'Tamworth',
    'Lichfield',
    'Cannock',
    'Stafford',
    'Stone',
    'Uttoxeter',
    'Leek',
    'Newcastle-under-Lyme',
    'Crewe',
    'Nantwich',
    'Congleton',
    'Macclesfield',
    'Wilmslow',
    'Knutsford',
    'Northwich',
    'Winsford',
    'Runcorn',
    'Widnes',
    'St Helens',
    'Southport',
    'Formby',
    'Crosby',
    'Bootle',
    'Wallasey',
    'Bebington',
    'Ellesmere Port',
    'Fleetwood',
    'Morecambe',
    'Lancaster',
    'Burnley',
    'Accrington',
    'Clitheroe',
    'Darwen',
    'Chorley',
    'Leyland',
    'Skelmersdale',
    'Ormskirk',
    'Bury',
    'Ashton-under-Lyne',
    'Hyde',
    'Stalybridge',
    'Denton',
    'Droylsden',
    'Failsworth',
    'Middleton',
    'Heywood',
    'Littleborough',
    'Todmorden',
    'Halifax',
    'Brighouse',
    'Elland',
    'Sowerby Bridge',
    'Dewsbury',
    'Batley',
    'Mirfield',
    'Heckmondwike',
    'Cleckheaton',
    'Bingley',
    'Keighley',
    'Shipley',
    'Ilkley',
    'Otley',
    'Wetherby',
    'Tadcaster',
    'Goole',
    'Pontefract',
    'Castleford',
    'Normanton',
    'Featherstone',
    'Hemsworth',
    'Darlington',
    'Stockton-on-Tees',
    'Hartlepool',
    'Redcar',
    'Billingham',
    'Yarm',
    'Guisborough',
    'Saltburn-by-the-Sea',
    'Bishop Auckland',
    'Newton Aycliffe',
    'Shildon',
    'Crook',
    'Consett',
    'Stanley',
    'Chester-le-Street',
    'Houghton-le-Spring',
    'Washington',
    'South Shields',
    'Jarrow',
    'Hebburn',
    'Gateshead',
    'Blaydon',
    'Whickham',
    'Prudhoe',
    'Hexham',
    'Morpeth',
    'Ashington',
    'Blyth',
    'Cramlington',
    'Whitley Bay',
    'Tynemouth',
    'North Shields',
    'Wallsend',
    'Berwick-upon-Tweed',
    'Alnwick',
    'Amble',
    'Carlisle',
    'Penrith',
    'Workington',
    'Whitehaven',
    'Maryport',
    'Cockermouth',
    'Keswick',
    'Kendal',
    'Windermere',
    'Barrow-in-Furness',
    'Ulverston',
    'Grange-over-Sands',
    'Shrewsbury',
    'Oswestry',
    'Whitchurch',
    'Market Drayton',
    'Newport',
    'Bridgnorth',
    'Ludlow',
    'Kidderminster',
    'Stourbridge',
    'Halesowen',
    'Dudley',
    'West Bromwich',
    'Walsall',
    'Sutton Coldfield',
    'Solihull',
    'Redditch',
    'Bromsgrove',
    'Droitwich',
    'Evesham',
    'Pershore',
    'Malvern',
    'Ledbury',
    'Ross-on-Wye',
    'Hereford',
    'Leominster',
    'Nuneaton',
    'Bedworth',
    'Hinckley',
    'Rugby',
    'Leamington Spa',
    'Warwick',
    'Stratford-upon-Avon',
    'Kenilworth',
    'Daventry',
    'Towcester',
    'Brackley',
    'Corby',
    'Kettering',
    'Wellingborough',
    'Rushden',
    'Raunds',
    'Thrapston',
    'Oundle',
    'Market Harborough',
    'Oakham',
    'Melton Mowbray',
    'Loughborough',
    'Coalville',
    'Hinckley',
    'Wigston',
    'Oadby',
    'Glenfield',
    'Long Eaton',
    'Ilkeston',
    'Heanor',
    'Ripley',
    'Belper',
    'Alfreton',
    'Swadlincote',
    'Ashby-de-la-Zouch',
    'Coalville',
    'Louth',
    'Horncastle',
    'Woodhall Spa',
    'Market Rasen',
    'Caistor',
    'Brigg',
    'Barton-upon-Humber',
    'Immingham',
    'Kings Lynn',
    'Hunstanton',
    'Downham Market',
    'Swaffham',
    'Dereham',
    'Fakenham',
    'Cromer',
    'Sheringham',
    'North Walsham',
    'Aylsham',
    'Wroxham',
    'Great Yarmouth',
    'Lowestoft',
    'Beccles',
    'Bungay',
    'Halesworth',
    'Southwold',
    'Aldeburgh',
    'Leiston',
    'Saxmundham',
    'Framlingham',
    'Woodbridge',
    'Felixstowe',
    'Harwich',
    'Manningtree',
    'Clacton-on-Sea',
    'Frinton-on-Sea',
    'Walton-on-the-Naze',
    'Brightlingsea',
    'Wivenhoe',
    'Halstead',
    'Braintree',
    'Witham',
    'Maldon',
    'Burnham-on-Crouch',
    'Rayleigh',
    'Benfleet',
    'Canvey Island',
    'Stanford-le-Hope',
    'Grays',
    'Tilbury',
    'Thurrock',
    'Romford',
    'Hornchurch',
    'Upminster',
    'Dagenham',
    'Barking',
    'Ilford',
    'Walthamstow',
    'Leyton',
    'Stratford',
    'West Ham',
    'East Ham',
    'Woolwich',
    'Greenwich',
    'Lewisham',
    'Catford',
    'Bromley',
    'Orpington',
    'Sidcup',
    'Bexleyheath',
    'Erith',
    'Crayford',
    'Welling',
    'Eltham',
    'Blackheath',
    'Deptford',
    'Peckham',
    'Camberwell',
    'Brixton',
    'Streatham',
    'Croydon',
    'Purley',
    'Coulsdon',
    'Caterham',
    'Redhill',
    'Reigate',
    'Dorking',
    'Leatherhead',
    'Epsom',
    'Ewell',
    'Sutton',
    'Cheam',
    'Carshalton',
    'Wallington',
    'Mitcham',
    'Morden',
    'Wimbledon',
    'Kingston upon Thames',
    'Surbiton',
    'New Malden',
    'Esher',
    'Weybridge',
    'Walton-on-Thames',
    'Shepperton',
    'Sunbury-on-Thames',
    'Staines-upon-Thames',
    'Ashford',
    'Feltham',
    'Hounslow',
    'Isleworth',
    'Brentford',
    'Chiswick',
    'Ealing',
    'Acton',
    'Hammersmith',
    'Fulham',
    'Putney',
    'Wandsworth',
    'Battersea',
    'Clapham',
    'Balham',
    'Tooting',
    'Southfields',
    'Richmond',
    'Twickenham',
    'Teddington',
    'Hampton',
    'Hanworth',
    'Uxbridge',
    'Ruislip',
    'Northwood',
    'Pinner',
    'Harrow',
    'Wembley',
    'Kingsbury',
    'Hendon',
    'Golders Green',
    'Finchley',
    'Barnet',
    'Edgware',
    'Stanmore',
    'Bushey',
    'Rickmansworth',
    'Chorleywood',
    'Amersham',
    'Chesham',
    'Beaconsfield',
    'Gerrards Cross',
    'Chalfont St Peter',
    'Chalfont St Giles',
    'Marlow',
    'Henley-on-Thames',
    'Maidenhead',
    'Windsor',
    'Eton',
    'Ascot',
    'Bracknell',
    'Wokingham',
    'Sandhurst',
    'Camberley',
    'Farnborough',
    'Aldershot',
    'Fleet',
    'Farnham',
    'Haslemere',
    'Godalming',
    'Cranleigh',
    'Horsham',
    'Billingshurst',
    'Pulborough',
    'Petworth',
    'Midhurst',
    'Chichester',
    'Bognor Regis',
    'Littlehampton',
    'Arundel',
    'Shoreham-by-Sea',
    'Lancing',
    'Hove',
    'Lewes',
    'Seaford',
    'Newhaven',
    'Peacehaven',
    'Uckfield',
    'Crowborough',
    'Heathfield',
    'Hailsham',
    'Polegate',
    'Bexhill-on-Sea',
    'Battle',
    'Rye',
    'Tenterden',
    'Cranbrook',
    'Paddock Wood',
    'Tonbridge',
    'Edenbridge',
    'Westerham',
    'Biggin Hill',
    'Swanley',
    'Welling',
    'Erith',
    'Andover',
    'Winchester',
    'Eastleigh',
    'Fareham',
    'Gosport',
    'Havant',
    'Waterlooville',
    'Petersfield',
    'Alton',
    'Bordon',
    'Liphook',
    'Lymington',
    'New Milton',
    'Christchurch',
    'Ringwood',
    'Fordingbridge',
    'Romsey',
    'Salisbury',
    'Amesbury',
    'Tidworth',
    'Devizes',
    'Trowbridge',
    'Melksham',
    'Bradford-on-Avon',
    'Chippenham',
    'Calne',
    'Corsham',
    'Marlborough',
    'Hungerford',
    'Newbury',
    'Thatcham',
    'Didcot',
    'Wallingford',
    'Abingdon',
    'Wantage',
    'Faringdon',
    'Witney',
    'Carterton',
    'Chipping Norton',
    'Woodstock',
    'Bicester',
    'Kidlington',
    'Thame',
    'Princes Risborough',
    'Wendover',
    'Tring',
    'Berkhamsted',
    'Harpenden',
    'Dunstable',
    'Leighton Buzzard',
    'Biggleswade',
    'Sandy',
    'St Neots',
    'Huntingdon',
    'St Ives',
    'Ramsey',
    'March',
    'Whittlesey',
    'Wisbech',
    'Ely',
    'Littleport',
    'Soham',
    'Newmarket',
    'Mildenhall',
    'Brandon',
    'Thetford',
    'Attleborough',
    'Wymondham',
    'Poole',
    'Weymouth',
    'Dorchester',
    'Bridport',
    'Lyme Regis',
    'Axminster',
    'Honiton',
    'Ottery St Mary',
    'Sidmouth',
    'Exmouth',
    'Budleigh Salterton',
    'Teignmouth',
    'Dawlish',
    'Newton Abbot',
    'Torquay',
    'Paignton',
    'Brixham',
    'Totnes',
    'Dartmouth',
    'Kingsbridge',
    'Salcombe',
    'Ivybridge',
    'Tavistock',
    'Okehampton',
    'Crediton',
    'Tiverton',
    'Cullompton',
    'Wellington',
    'Taunton',
    'Bridgwater',
    'Burnham-on-Sea',
    'Highbridge',
    'Cheddar',
    'Wells',
    'Glastonbury',
    'Street',
    'Shepton Mallet',
    'Frome',
    'Radstock',
    'Midsomer Norton',
    'Keynsham',
    'Clevedon',
    'Portishead',
    'Nailsea',
    'Yate',
    'Thornbury',
    'Chipping Sodbury',
    'Stroud',
    'Dursley',
    'Wotton-under-Edge',
    'Nailsworth',
    'Cirencester',
    'Tetbury',
    'Fairford',
    'Lechlade',
    'Stow-on-the-Wold',
    'Moreton-in-Marsh',
    'Bourton-on-the-Water',
    'Tewkesbury',
    'Winchcombe',
    'Bishops Cleeve',
    'Barnstaple',
    'Ilfracombe',
    'Bideford',
    'Great Torrington',
    'South Molton',
    'Lynton',
    'Minehead',
    'Watchet',
    'Williton',
    'Dulverton',
    'Yeovil',
    'Crewkerne',
    'Chard',
    'Ilminster',
    'Langport',
    'Somerton',
    'Wincanton',
    'Castle Cary',
    'Bruton',
    'Warminster',
    'Westbury',
    'Mere',
    'Gillingham',
    'Shaftesbury',
    'Blandford Forum',
    'Wimborne Minster',
    'Ferndown',
    'Verwood',
    'Wareham',
    'Swanage',
    'Sherborne',
    'Sturminster Newton',
    'Beaminster',
    'Launceston',
    'Bodmin',
    'Wadebridge',
    'Padstow',
    'Newquay',
    'St Austell',
    'Fowey',
    'Looe',
    'Liskeard',
    'Callington',
    'Saltash',
    'Torpoint',
    'Falmouth',
    'Penryn',
    'Truro',
    'Redruth',
    'Camborne',
    'Hayle',
    'St Ives',
    'Penzance',
    'Helston',
    'Bude',
    // Scotland
    'Glasgow',
    'Edinburgh',
    'Aberdeen',
    'Dundee',
    'Inverness',
    'Stirling',
    'Perth',
    'Paisley',
    'East Kilbride',
    'Livingston',
    'Hamilton',
    'Cumbernauld',
    'Dunfermline',
    'Kirkcaldy',
    'Ayr',
    'Kilmarnock',
    'Greenock',
    'Coatbridge',
    'Glenrothes',
    'Airdrie',
    'Falkirk',
    'Irvine',
    'Motherwell',
    'Wishaw',
    'Rutherglen',
    'Bearsden',
    'Newton Mearns',
    'Bishopbriggs',
    'Bellshill',
    'Bathgate',
    'Arbroath',
    'Clydebank',
    'Dumfries',
    'Musselburgh',
    'Kirkintilloch',
    'Johnstone',
    'Penicuik',
    'Elgin',
    'Dumbarton',
    'Peterhead',
    'Grangemouth',
    'Fraserburgh',
    'Alloa',
    'Kelso',
    'Hawick',
    'Galashiels',
    'Jedburgh',
    'Selkirk',
    'Peebles',
    'Melrose',
    'Coldstream',
    'Eyemouth',
    'Duns',
    'Berwick-upon-Tweed',
    'Fort William',
    'Oban',
    'Campbeltown',
    'Dunoon',
    'Rothesay',
    'Largs',
    'Ardrossan',
    'Saltcoats',
    'Kilwinning',
    'Troon',
    'Prestwick',
    'Maybole',
    'Girvan',
    'Stranraer',
    'Newton Stewart',
    'Kirkcudbright',
    'Castle Douglas',
    'Dalbeattie',
    'Annan',
    'Lockerbie',
    'Moffat',
    'Sanquhar',
    'Lanark',
    'Biggar',
    'Carluke',
    'Shotts',
    'Whitburn',
    'Broxburn',
    'Linlithgow',
    'Bo\'ness',
    'Denny',
    'Bonnybridge',
    'Larbert',
    'Stenhousemuir',
    'Tillicoultry',
    'Alva',
    'Dollar',
    'Clackmannan',
    'Kinross',
    'Crieff',
    'Pitlochry',
    'Aberfeldy',
    'Dunkeld',
    'Blairgowrie',
    'Coupar Angus',
    'Kirriemuir',
    'Forfar',
    'Brechin',
    'Montrose',
    'Stonehaven',
    'Laurencekirk',
    'Inverurie',
    'Kintore',
    'Westhill',
    'Portlethen',
    'Banchory',
    'Ballater',
    'Braemar',
    'Huntly',
    'Keith',
    'Buckie',
    'Cullen',
    'Banff',
    'Macduff',
    'Turriff',
    'Nairn',
    'Forres',
    'Lossiemouth',
    'Dingwall',
    'Tain',
    'Invergordon',
    'Alness',
    'Wick',
    'Thurso',
    'Kirkwall',
    'Stromness',
    'Lerwick',
    'Portree',
    'Stornoway',
    // Wales
    'Cardiff',
    'Swansea',
    'Newport',
    'Wrexham',
    'Barry',
    'Neath',
    'Port Talbot',
    'Cwmbran',
    'Llanelli',
    'Bridgend',
    'Caerphilly',
    'Pontypridd',
    'Merthyr Tydfil',
    'Aberdare',
    'Rhondda',
    'Tonypandy',
    'Treorchy',
    'Mountain Ash',
    'Ferndale',
    'Porth',
    'Maesteg',
    'Porthcawl',
    'Pencoed',
    'Cowbridge',
    'Penarth',
    'Dinas Powys',
    'Llandaff',
    'Pontyclun',
    'Llantrisant',
    'Talbot Green',
    'Bargoed',
    'Blackwood',
    'Risca',
    'Abertillery',
    'Tredegar',
    'Ebbw Vale',
    'Blaenavon',
    'Pontypool',
    'Abergavenny',
    'Monmouth',
    'Chepstow',
    'Caldicot',
    'Usk',
    'Brecon',
    'Crickhowell',
    'Hay-on-Wye',
    'Builth Wells',
    'Llandrindod Wells',
    'Rhayader',
    'Newtown',
    'Welshpool',
    'Llanfyllin',
    'Oswestry',
    'Llangollen',
    'Corwen',
    'Bala',
    'Dolgellau',
    'Barmouth',
    'Tywyn',
    'Machynlleth',
    'Aberystwyth',
    'Lampeter',
    'Tregaron',
    'Cardigan',
    'Newcastle Emlyn',
    'Llandysul',
    'Carmarthen',
    'St Clears',
    'Whitland',
    'Narberth',
    'Tenby',
    'Saundersfoot',
    'Pembroke',
    'Pembroke Dock',
    'Milford Haven',
    'Haverfordwest',
    'Fishguard',
    'St Davids',
    'Goodwick',
    'Ammanford',
    'Llandeilo',
    'Llandovery',
    'Kidwelly',
    'Burry Port',
    'Gorseinon',
    'Pontarddulais',
    'Clydach',
    'Morriston',
    'Sketty',
    'Mumbles',
    'Gowerton',
    'Loughor',
    'Penclawdd',
    'Rhyl',
    'Prestatyn',
    'Denbigh',
    'Ruthin',
    'Mold',
    'Flint',
    'Holywell',
    'Buckley',
    'Connah\'s Quay',
    'Queensferry',
    'Shotton',
    'Hawarden',
    'Caernarfon',
    'Bangor',
    'Bethesda',
    'Llanberis',
    'Porthmadog',
    'Pwllheli',
    'Criccieth',
    'Harlech',
    'Blaenau Ffestiniog',
    'Llandudno',
    'Conwy',
    'Colwyn Bay',
    'Abergele',
    'Llanrwst',
    'Betws-y-Coed',
    'Holyhead',
    'Llangefni',
    'Beaumaris',
    'Menai Bridge',
    'Amlwch',
    // Northern Ireland
    'Belfast',
    'Derry',
    'Londonderry',
    'Lisburn',
    'Newry',
    'Bangor',
    'Craigavon',
    'Newtownabbey',
    'Castlereagh',
    'Ballymena',
    'Newtownards',
    'Carrickfergus',
    'Coleraine',
    'Omagh',
    'Larne',
    'Antrim',
    'Enniskillen',
    'Banbridge',
    'Strabane',
    'Limavady',
    'Holywood',
    'Dungannon',
    'Cookstown',
    'Downpatrick',
    'Ballymoney',
    'Comber',
    'Magherafelt',
    'Portstewart',
    'Portrush',
    'Newcastle',
    'Warrenpoint',
    'Kilkeel',
    'Ballycastle',
    'Cushendall',
    'Bushmills',
    'Armagh',
    'Lurgan',
    'Portadown',
    'Newry',
    'Dromore',
    'Hillsborough',
    'Moira',
];

// USA Cities
export const USA_CITIES = [
    // Major Cities
    'New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix',
    'Philadelphia', 'San Antonio', 'San Diego', 'Dallas', 'San Jose',
    'Austin', 'Jacksonville', 'Fort Worth', 'Columbus', 'Charlotte',
    'San Francisco', 'Indianapolis', 'Seattle', 'Denver', 'Boston',
    'El Paso', 'Nashville', 'Detroit', 'Portland', 'Las Vegas',
    'Memphis', 'Louisville', 'Baltimore', 'Milwaukee', 'Albuquerque',
    'Tucson', 'Fresno', 'Sacramento', 'Atlanta', 'Miami',
    'Kansas City', 'Colorado Springs', 'Raleigh', 'Omaha', 'Long Beach',
    'Virginia Beach', 'Oakland', 'Minneapolis', 'Tampa', 'Tulsa',
    'Arlington', 'New Orleans', 'Wichita', 'Cleveland', 'Bakersfield',
    'Aurora', 'Anaheim', 'Honolulu', 'Santa Ana', 'Riverside',
    'Corpus Christi', 'Lexington', 'Pittsburgh', 'Cincinnati', 'Anchorage',
    'Stockton', 'Toledo', 'St Paul', 'Newark', 'Greensboro',
    'Buffalo', 'Plano', 'Lincoln', 'Henderson', 'Fort Wayne',
    'Jersey City', 'St Petersburg', 'Chula Vista', 'Norfolk', 'Orlando',
    'Chandler', 'Laredo', 'Madison', 'Durham', 'Lubbock',
    'Winston-Salem', 'Garland', 'Glendale', 'Hialeah', 'Reno',
    'Baton Rouge', 'Irvine', 'Chesapeake', 'Irving', 'Scottsdale',
    'North Las Vegas', 'Fremont', 'Gilbert', 'San Bernardino', 'Boise',
    'Birmingham', 'Rochester', 'Richmond', 'Spokane', 'Des Moines',
];

// Canada Cities
export const CANADA_CITIES = [
    // Major Cities
    'Toronto', 'Montreal', 'Vancouver', 'Calgary', 'Edmonton',
    'Ottawa', 'Winnipeg', 'Quebec City', 'Hamilton', 'Kitchener',
    'London', 'Victoria', 'Halifax', 'Oshawa', 'Windsor',
    'Saskatoon', 'Regina', 'St Johns', 'Barrie', 'Kelowna',
    'Abbotsford', 'Kingston', 'Milton', 'Thunder Bay', 'Waterloo',
    'Guelph', 'Cambridge', 'Whitby', 'Ajax', 'Langley',
    'Saanich', 'Terrebonne', 'Moncton', 'Nanaimo', 'Kamloops',
    'Brantford', 'Saint John', 'Chatham-Kent', 'Red Deer', 'Lethbridge',
    'Fredericton', 'Peterborough', 'Kawartha Lakes', 'Sarnia', 'Drummondville',
    'Prince George', 'Chilliwack', 'Belleville', 'Sault Ste Marie', 'Sherbrooke',
    'Trois-Rivières', 'Saint-Jean-sur-Richelieu', 'Granby', 'Joliette', 'Shawinigan',
    'Dollard-des-Ormeaux', 'Gatineau', 'Laval', 'Longueuil', 'Markham',
    'Brampton', 'Vaughan', 'Richmond Hill', 'Oakville', 'Burlington',
    'Mississauga', 'Scarborough', 'Etobicoke', 'North York', 'Pickering',
];

// Australia Cities
export const AUSTRALIA_CITIES = [
    // Major Cities
    'Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide',
    'Gold Coast', 'Newcastle', 'Canberra', 'Wollongong', 'Sunshine Coast',
    'Hobart', 'Geelong', 'Townsville', 'Cairns', 'Darwin',
    'Toowoomba', 'Ballarat', 'Bendigo', 'Launceston', 'Mackay',
    'Rockhampton', 'Bunbury', 'Bundaberg', 'Hervey Bay', 'Wagga Wagga',
    'Coffs Harbour', 'Gladstone', 'Mildura', 'Shepparton', 'Tamworth',
    'Port Macquarie', 'Orange', 'Dubbo', 'Geraldton', 'Nowra',
    'Bathurst', 'Warrnambool', 'Albany', 'Kalgoorlie', 'Mount Gambier',
    'Lismore', 'Maitland', 'Armidale', 'Goulburn', 'Broken Hill',
    'Whyalla', 'Murray Bridge', 'Victor Harbor', 'Mount Barker', 'Gawler',
    'Alice Springs', 'Katherine', 'Palmerston', 'Broome', 'Karratha',
    'Port Hedland', 'Busselton', 'Mandurah', 'Fremantle', 'Rockingham',
    'Joondalup', 'Wanneroo', 'Stirling', 'Melville', 'Gosnells',
    'Parramatta', 'Blacktown', 'Liverpool', 'Penrith', 'Campbelltown',
];

// Combined cities by country
export const CITIES_BY_COUNTRY = {
    'United Kingdom': UK_CITIES,
    'United States': USA_CITIES,
    'Canada': CANADA_CITIES,
    'Australia': AUSTRALIA_CITIES,
};

// Supported countries for Multi-Apply
export const SUPPORTED_COUNTRIES = [
    'United Kingdom',
    'United States',
    'Canada',
    'Australia',
];

export const EXPERIENCE_LEVELS = [
    { value: 'entry', label: 'Entry Level' },
    { value: 'mid', label: 'Mid Level' },
    { value: 'senior', label: 'Senior Level' },
];

export const WORK_MODES = [
    { value: 'remote', label: 'Remote' },
    { value: 'hybrid', label: 'Hybrid' },
    { value: 'on-site', label: 'On-site' },
];
